import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TableModule } from 'primeng/table';
import { Tabs, TabList, Tab } from 'primeng/tabs';
import { ReportResultResponse } from '@features/reports/models/results.model';
import { CommonModule } from '@angular/common';
import sourcesData from '@assets/data/sources.json';

interface TableColumn {
  field: string;
  header: string;
}

interface GroupedData {
  groupKey: string;
  consolidatedItems: ReportResultResponse[];
  totals: {
    Co2: number;
    Ch4: number;
    Ch4Bio: number;
    N2o: number;
    Co2Bio: number;
    Hfc: number;
    Pfc: number;
    Sf6: number;
    Nf3: number;
    NoKyoto: number;
    TotalCo2e: number;
    Uncertainty: number;
    TotalT: number;
    ScopeCategoryPct: number;
    InventoryPct: number;
  };
}

@Component({
  selector: 'app-results-list',
  imports: [TableModule, Tabs, TabList, Tab, CommonModule],
  templateUrl: './results-list.html',
  styleUrl: './results-list.scss',
})
export class ResultsList implements OnChanges {
  @Input() data: ReportResultResponse[] = [];

  get columns(): TableColumn[] {
    return [
      { field: 'Variation', header: 'Fuentes de emisión' },
      { field: 'Co2', header: 'tCO₂' },
      { field: 'Ch4', header: 'tCH₄' },
      { field: 'Ch4Bio', header: 'tCH₄ Biogénico' },
      { field: 'N2o', header: 'tN₂O' },
      { field: 'Co2Bio', header: 'tCO₂ Biogénico' },
      { field: 'Hfc', header: 'tHFC'},
      { field: 'Pfc', header: 'tPFC'},
      { field: 'Sf6', header: 'tSF₆'},
      { field: 'Nf3', header: 'tNF₃'},
      { field: 'NoKyoto', header: 't(GEI no Kyoto)'},
      { field: 'TotalCo2e', header: 'tCO₂e'},
      { field: 'ScopeCategoryPct', header: this.activeTabIndex === 0 ? '% Alcance' : '% Categoría' },
      { field: 'InventoryPct', header: '% Inventario GEI' },
      { field: 'Uncertainty', header: '% Incertidumbre' },
      { field: 'TotalT', header: 'Cálculo Auxiliar' },
    ];
  }

  displayData: any[] = [];
  groupedByScope: GroupedData[] = [];
  groupedByCategory: GroupedData[] = [];
  activeTabIndex: number = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data.length > 0) {
        this.displayData = this.data;
        this.groupDataByScope();
        this.groupDataByCategory();
      } else {
        // Limpiar datos cuando data está vacío
        this.displayData = [];
        this.groupedByScope = [];
        this.groupedByCategory = [];
      }
    }
  }

  private groupDataByScope(): void {
    const grouped = new Map<string, ReportResultResponse[]>();

    this.data.forEach(item => {
      const scope = item.Scope || 'Sin Alcance';
      if (!grouped.has(scope)) {
        grouped.set(scope, []);
      }
      grouped.get(scope)!.push(item);
    });

    this.groupedByScope = Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([groupKey, items]) => {
        const consolidatedItems = this.groupByVariation(items);
        console.log("groupByVariation", consolidatedItems)
        return {
          groupKey: `Alcance ${groupKey}`,
          consolidatedItems,
          totals: this.calculateTotals(items)
        };
      });

      console.log("groupedByScope", this.groupedByScope)
  }

  private groupDataByCategory(): void {
    const grouped = new Map<string, ReportResultResponse[]>();

    this.data.forEach(item => {
      const category = item.Category || 'Sin Categoría';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(item);
    });

    this.groupedByCategory = Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([groupKey, items]) => {
        const consolidatedItems = this.groupByVariation(items);
        return {
          groupKey: `Categoría ${groupKey}`,
          consolidatedItems,
          totals: this.calculateTotals(items)
        };
      });

      console.log("groupedByCategory", this.groupedByCategory)
  }

  private groupByVariation(items: ReportResultResponse[]): ReportResultResponse[] {
    const grouped = new Map<string, ReportResultResponse[]>();

    items.forEach(item => {
      const rawVariation = item.Variation || 'Sin categoría';
      const translation = (sourcesData.variations as Record<string, string>)[rawVariation];
      const variation = translation || rawVariation;

      if (!grouped.has(variation)) {
        grouped.set(variation, []);
      }
      grouped.get(variation)!.push(item);
    });

    // Consolidar cada grupo de variación en un solo registro
    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([variation, subItems]) => {
        const firstItem = subItems[0];
        const totals = this.calculateTotals(subItems);

        // Crear un registro consolidado con todos los valores sumados
        return {
          ...firstItem,
          Variation: variation,
          Co2: totals.Co2,
          Ch4: totals.Ch4,
          Ch4Bio: totals.Ch4Bio,
          N2o: totals.N2o,
          Co2Bio: totals.Co2Bio,
          Hfc: totals.Hfc,
          Pfc: totals.Pfc,
          Sf6: totals.Sf6,
          Nf3: totals.Nf3,
          NoKyoto: totals.NoKyoto,
          TotalCo2e: totals.TotalCo2e,
          Uncertainty: totals.Uncertainty,
          TotalT: totals.TotalT,
          ScopeCategoryPct: totals.ScopeCategoryPct,
          InventoryPct: totals.InventoryPct
        };
      });
  }

  private calculateTotals(items: ReportResultResponse[]) {
    return items.reduce((acc, item) => ({
      Co2: acc.Co2 + (Number(item.Co2) || 0),
      Ch4: acc.Ch4 + (Number(item.Ch4) || 0),
      Ch4Bio: acc.Ch4Bio + (Number(item.Ch4Bio) || 0),
      N2o: acc.N2o + (Number(item.N2o) || 0),
      Co2Bio: acc.Co2Bio + (Number(item.Co2Bio) || 0),
      Hfc: acc.Hfc + (Number((item as any).Hfc) || 0),
      Pfc: acc.Pfc + (Number((item as any).Pfc) || 0),
      Sf6: acc.Sf6 + (Number((item as any).Sf6) || 0),
      Nf3: acc.Nf3 + (Number((item as any).Nf3) || 0),
      NoKyoto: acc.NoKyoto + (Number((item as any).NoKyoto) || 0),
      TotalCo2e: acc.TotalCo2e + (Number((item as any).TotalCo2e) || 0),
      Uncertainty: acc.Uncertainty + (Number(item.Uncertainty) || 0),
      TotalT: acc.TotalT + (Number(item.TotalT) || 0),
      ScopeCategoryPct: acc.ScopeCategoryPct + (Number((item as any).ScopeCategoryPct) || 0),
      InventoryPct: acc.InventoryPct + (Number((item as any).InventoryPct) || 0)
    }), { Co2: 0, Ch4: 0, Ch4Bio: 0, N2o: 0, Co2Bio: 0, Hfc: 0, Pfc: 0, Sf6: 0, Nf3: 0, NoKyoto: 0, TotalCo2e: 0, Uncertainty: 0, TotalT: 0, ScopeCategoryPct: 0, InventoryPct: 0 });
  }

  get currentGroupedData(): GroupedData[] {
    return this.activeTabIndex === 0 ? this.groupedByScope : this.groupedByCategory;
  }

  get grandTotal() {
    return this.currentGroupedData.reduce((acc, group) => ({
      Co2: acc.Co2 + group.totals.Co2,
      Ch4: acc.Ch4 + group.totals.Ch4,
      Ch4Bio: acc.Ch4Bio + group.totals.Ch4Bio,
      N2o: acc.N2o + group.totals.N2o,
      Co2Bio: acc.Co2Bio + group.totals.Co2Bio,
      Hfc: acc.Hfc + group.totals.Hfc,
      Pfc: acc.Pfc + group.totals.Pfc,
      Sf6: acc.Sf6 + group.totals.Sf6,
      Nf3: acc.Nf3 + group.totals.Nf3,
      NoKyoto: acc.NoKyoto + group.totals.NoKyoto,
      TotalCo2e: acc.TotalCo2e + group.totals.TotalCo2e,
      Uncertainty: acc.Uncertainty + group.totals.Uncertainty,
      TotalT: acc.TotalT + group.totals.TotalT,
      ScopeCategoryPct: acc.ScopeCategoryPct + group.totals.ScopeCategoryPct,
      InventoryPct: acc.InventoryPct + group.totals.InventoryPct
    }), { Co2: 0, Ch4: 0, Ch4Bio: 0, N2o: 0, Co2Bio: 0, Hfc: 0, Pfc: 0, Sf6: 0, Nf3: 0, NoKyoto: 0, TotalCo2e: 0, Uncertainty: 0, TotalT: 0, ScopeCategoryPct: 0, InventoryPct: 0 });
  }

  get tableData(): any[] {
    const result: any[] = [];
    const grandTotalCo2e = this.grandTotal.TotalCo2e;

    this.currentGroupedData.forEach(group => {
      // Agregar fila de header de grupo
      result.push({ isGroupHeader: true, groupKey: group.groupKey });

      const groupTotalCo2e = group.totals.TotalCo2e;

      // Agregar filas consolidadas con porcentajes calculados
      const consolidadosConPorcentajes = group.consolidatedItems.map(item => {
        const itemCo2e = Number((item as any).TotalCo2e) || 0;
        const groupTotalCo2e = Number(group.totals.TotalCo2e) || 0;

        return {
          ...item,
          ScopeCategoryPct: groupTotalCo2e ? itemCo2e / groupTotalCo2e : 0,
          InventoryPct: grandTotalCo2e ? itemCo2e / grandTotalCo2e : 0
        };
      });

      // Recalcular los porcentajes para el total del grupo
      const groupTotalInventoryPct = grandTotalCo2e ? groupTotalCo2e / grandTotalCo2e : 0;
      group.totals.ScopeCategoryPct = 1; // El total del grupo siempre es 100% de sí mismo
      group.totals.InventoryPct = groupTotalInventoryPct;

      result.push(...consolidadosConPorcentajes);

      // Agregar fila de totales del grupo
      result.push({ isTotal: true, totals: group.totals });
    });

    const finalGrandTotal = this.grandTotal;
    // Recalcular porcentajes para el gran total
    finalGrandTotal.ScopeCategoryPct = 1; // 100%
    finalGrandTotal.InventoryPct = 1; // 100%

    // Agregar fila de gran total al final
    result.push({ isGrandTotal: true, totals: finalGrandTotal });
    return result;
  }

  isGroupHeader(row: any): boolean {
    return row?.isGroupHeader === true;
  }

  isTotal(row: any): boolean {
    return row?.isTotal === true;
  }

  isGrandTotal(row: any): boolean {
    return row?.isGrandTotal === true;
  }

  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
  }
}
